import React from "react";
import PropTypes from "prop-types";
import { useBudgetDetail } from "./useForecastExplorer";
import ForecastChart from "../../forecast/ForecastChart";
import { fmtEurSigned, humanizeUnits } from "./format";

export default function BudgetDetail({
  budgetId,
  budgetName,
  budgetKind,
  horizon,
  onDrillSeries,
  // Shell may also pass data/isLoading directly from its own query
  data: dataProp,
  isLoading: isLoadingProp,
}) {
  // Fetch via hook only when the parent hasn't wired data/loading at all. Treat
  // the props as "not provided" only when strictly undefined, so a parent that
  // explicitly passes data={null}/isLoading={false} doesn't trigger a duplicate fetch.
  const parentDrivesData = dataProp !== undefined || isLoadingProp !== undefined;
  const ownQuery = useBudgetDetail(
    !parentDrivesData && budgetId ? budgetId : null,
    horizon
  );

  const effectiveData = dataProp ?? ownQuery.data ?? null;
  const effectiveLoading = isLoadingProp ?? ownQuery.isLoading ?? false;

  // Map trajectory → ForecastChart series shape ({date, forecast, budget?}).
  // For owner rows, budget is absent — ForecastChart degrades gracefully (no cap line).
  // actual/lower/upper are omitted — this is purely a forward outlook.
  const chartSeries = React.useMemo(
    () =>
      (effectiveData?.trajectory || []).map((pt) => ({
        date: pt.date,
        forecast: pt.forecast,
        ...(pt.budget != null && { budget: pt.budget }),
      })),
    [effectiveData]
  );

  // Sort contributors by absolute growth_eur desc.
  const sortedContributors = React.useMemo(
    () =>
      [...(effectiveData?.contributors || [])].sort(
        (a, b) => Math.abs(b.growth_eur || 0) - Math.abs(a.growth_eur || 0)
      ),
    [effectiveData]
  );

  if (!budgetId) {
    return (
      <div className="flex flex-col items-center justify-center rounded-card border border-dashed border-border bg-surface-2 py-16 text-center">
        <span className="text-3xl" role="img" aria-label="budget">
          💰
        </span>
        <p className="mt-3 text-sm font-medium text-text-2">Select a budget</p>
        <p className="mt-1 text-xs text-text-3">
          Click any row in the table to see the budget outlook.
        </p>
      </div>
    );
  }

  if (effectiveLoading) {
    return (
      <div className="flex items-center justify-center rounded-card border border-card-bd bg-card py-16">
        <span className="text-sm text-text-3">
          Loading outlook for &ldquo;{budgetName || budgetId}&rdquo;…
        </span>
      </div>
    );
  }

  if (!effectiveData) {
    return (
      <div className="flex items-center justify-center rounded-card border border-card-bd bg-card py-16">
        <span className="text-sm text-text-3">
          No data available for &ldquo;{budgetName || budgetId}&rdquo;.
        </span>
      </div>
    );
  }

  const isOwner = budgetKind === "owner";
  const displayName = budgetName || String(budgetId);

  return (
    <div className="space-y-4">
      {/* Header strip */}
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-card border border-card-bd bg-card px-5 py-4">
        <div>
          <div className="text-base font-semibold text-text">{displayName}</div>
          <div className="mt-0.5 text-xs text-text-3">
            {isOwner ? "Forecast trajectory (no cap)" : "Budget outlook"}
          </div>
        </div>
        {isOwner && (
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-surface-2 text-text-3 italic self-start mt-0.5">
            no cap
          </span>
        )}
      </div>

      {/* Outlook chart — forecast vs the daily cap line */}
      {chartSeries.length > 0 && (
        <ForecastChart series={chartSeries} horizon={horizon} />
      )}

      {/* "Why it'll move" — constituent contributors (informational only) */}
      {sortedContributors.length > 0 && (
        <div className="rounded-card border border-card-bd bg-card px-5 py-4">
          <div className="mb-1">
            <div className="text-xs font-medium uppercase tracking-wide text-text-3">
              Why it&rsquo;ll move
            </div>
          </div>
          <ul className="mt-2 space-y-1">
            {sortedContributors.map((c, i) => {
              const name = humanizeUnits(c.source ?? c.unit ?? c.sku ?? "Unknown");
              const delta = c.growth_eur;
              const positive = (delta || 0) > 0;
              const drillable = !!c.series && !!onDrillSeries;

              return (
                <li
                  key={`${name}-${i}`}
                  className={`flex w-full items-center justify-between rounded-ctrl px-2 py-1.5 text-sm ${
                    drillable
                      ? "cursor-pointer hover:bg-surface-2 group"
                      : ""
                  }`}
                  onClick={drillable ? () => onDrillSeries(c.series) : undefined}
                  title={drillable ? `Drill into series: ${c.series}` : undefined}
                >
                  <span className={`${drillable ? "text-primary group-hover:underline" : "text-text-2"}`}>
                    {name}
                  </span>
                  <span
                    className={`tabular-nums font-medium ${
                      delta != null && positive
                        ? "text-danger"
                        : delta != null && delta < 0
                        ? "text-success"
                        : "text-text-3"
                    }`}
                  >
                    {fmtEurSigned(delta)}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="mt-3 text-xs text-text-3">
            Sources ranked by projected growth (forecast − baseline run-rate over the horizon).
            {onDrillSeries && " Click a highlighted source to drill into its cost-series detail."}
          </p>
        </div>
      )}

      {sortedContributors.length === 0 && chartSeries.length > 0 && (
        <div className="rounded-card border border-card-bd bg-card px-5 py-4">
          <p className="text-sm text-text-3">No contributor data available.</p>
        </div>
      )}
    </div>
  );
}

BudgetDetail.propTypes = {
  budgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  budgetName: PropTypes.string,
  budgetKind: PropTypes.string,
  horizon: PropTypes.number,
  onDrillSeries: PropTypes.func,
  data: PropTypes.shape({
    trajectory: PropTypes.array,
    contributors: PropTypes.array,
  }),
  isLoading: PropTypes.bool,
};
